import { Viewer } from '../Viewer'
import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';

/**
 * The Overlay class is used for any UI overlay that should be created in screenspace.
 * For any UI/Overlays in world space see ./SVGOverlay
 */
export class Overlay {
    _parentContainer: HTMLElement;
    _fontloader: FontLoader;


    private _activeMenu: HTMLElement | null = null;
    public get MenuActive(): boolean { return (this._activeMenu !== undefined); }
    constructor(private _viewer: Viewer, parentContainer: HTMLElement, dimensions?: { width: number, height: number }) {
        this._parentContainer = parentContainer;
        this._fontloader = new FontLoader();
    }

    createWorldSpaceText(text: string, params: { color?: number, opacity?: number, font?: string, size?: number }): Promise<THREE.Mesh> {
        let result;
        let font =  params.font;
        if(!font)
            return Promise.reject("No font provided");

        return new Promise((resolve, reject) => {
            this._fontloader.load(font!, (loadedFont) => {
                // Props
                let color = (params.color) ? params.color : 0x000000;
                let opacity = (params.opacity) ? params.opacity : 1;
                let transparent = (!!params.opacity && params.opacity < 1);
                let size = (params.size) ? params.size : 50;
                // Material
                const matLite = new THREE.MeshBasicMaterial({
                    color: color,
                    transparent: transparent,
                    opacity: opacity,
                    side: THREE.DoubleSide
                });
                // Shape
                const shapes = loadedFont.generateShapes(text, size);
                const geometry = new THREE.ShapeGeometry(shapes);
                geometry.computeBoundingBox();
                if (geometry.boundingBox) {
                    const xMid = - 0.5 * (geometry.boundingBox.max.x - geometry.boundingBox.min.x);
                    geometry.translate(xMid, 0, 0);
                }
                // Out
                result = new THREE.Mesh(geometry, matLite);
                resolve(result);
            }, () => { }, (error) => {
                reject(error);
            })
        });

    }

    createTextBubble(screenPos: { left?: number, top?: number, right?: number, bottom?: number }, size: { x: number, y: number }, message: string, title?: string) {
        const node = document.createElement('rect');
        node.style.position = 'absolute';
        node.style.display = 'grid';
        node.style.stroke = 'black';
        node.style.background = 'white';
        node.style.borderRadius = '4px';
        node.style.width = size.x + 'px';
        node.style.height = size.y + 'px';
        node.style.left = screenPos.left ? (screenPos.left * 100).toString() + "%" : '';
        node.style.top = screenPos.top ? (screenPos.top * 100).toString() + "%" : '';
        node.style.right = screenPos.right ? (screenPos.right * 100).toString() + "%" : '';
        node.style.bottom = screenPos.bottom ? (screenPos.bottom * 100).toString() + "%" : '';

        node.style.pointerEvents = 'none';

        // Title
        if (title) {
            const titleNode = document.createElement('text');
            titleNode.setAttribute('fill', 'white');
            titleNode.setAttribute('font', "monospace " + size.y * 0.7 + "px");
            titleNode.setAttribute('paint-order', 'stroke');
            titleNode.setAttribute('stroke', 'black');
            titleNode.textContent = title;
            node.appendChild(titleNode);
        }

        // Message

        const messageNode = document.createElement('text');
        messageNode.setAttribute('fill', 'white');
        messageNode.setAttribute('font', "monospace " + size.y * 0.7 + "px");
        messageNode.setAttribute('paint-order', 'stroke');
        messageNode.setAttribute('stroke', 'black');
        messageNode.textContent = message;
        node.appendChild(messageNode);

        this._parentContainer.appendChild(node);
        return node;

    }

    createMenu(screenPos: { x: number, y: number }, size: { x: number, y: number }, inButtons: { text: string, action: (any) => any, type?: string }[], title?: string) {
        const node = document.createElement('rect');
        node.style.position = 'absolute';
        node.style.display = 'grid';
        node.style.stroke = 'black';
        node.style.background = 'grey';
        node.style.borderRadius = '4px';
        node.style.left = ((screenPos.x + 1) / 2 * 100).toString() + "%";
        node.style.bottom = ((screenPos.y + 1) / 2 * 100).toString() + "%";
        node.style.pointerEvents = 'none';


        // Title
        if (title) {
            const titleNode = document.createElement('text');
            titleNode.setAttribute('fill', 'white');
            titleNode.setAttribute('font', "monospace " + size.y * 0.7 + "px");
            titleNode.setAttribute('paint-order', 'stroke');
            titleNode.setAttribute('stroke', 'black');
            titleNode.textContent = title;
        }

        let buttons = this.createMenuButtons(size, inButtons);
        buttons.forEach(button => {
            node.appendChild(button);
        });
        this._parentContainer.appendChild(node);
        this._activeMenu = node;
    }

    createMenuButtons(size: { x: number, y: number }, inButtons: { text: string, action: (any) => any, type?: string }[]): Array<HTMLElement> {
        let self = this;
        let buttons: HTMLElement[] = [];
        // Button
        inButtons.forEach((inButton, index) => {
            // The actual button
            let button: HTMLElement;
            if (!inButton.type || inButton.type === 'button') {
                button = document.createElement('button');
                button.addEventListener('mouseenter', onButtonEnter, { once: true });
                button.style.width = (size.x * 0.95).toString() + "px";
                button.style.height = (size.y * 0.90).toString() + "px";
                button.style.borderRadius = '2.5px';
                button.style.background = 'lightgray';
                button.innerHTML = inButton.text;
                button.addEventListener('click', inButton.action);
                button.addEventListener('click', self.clearMenu.bind(self));

            }
            else if (inButton.type === 'toggle') {
                button = document.createElement('rect');
                button.style.width = (size.x * 0.95).toString() + "px";
                button.style.height = (size.y * 0.90).toString() + "px";
                button.style.display = 'block';
                button.style.borderRadius = '2.5px';
                button.style.background = 'lightgray';
                button.style.textAlign = 'center';
                button.innerHTML = inButton.text;
                let toggle = document.createElement('input');
                toggle.setAttribute('type', 'checkbox');
                //toggle.classList.add("pull-right");
                button.innerHTML = inButton.text;
                button.appendChild(toggle);
                button.addEventListener('click', function () { inButton.action(toggle.checked) });
            }
            else
                return;

            // Button needs to receive pointer events whereas the parent does not
            button.style.pointerEvents = 'auto';
            // Funcs
            // Implementation of changing color when hovering over button
            function onButtonEnter() {
                button.style.background = 'darkgrey';
                button.style.color = 'white'
                button.addEventListener('mouseleave', onButtonLeave, { once: true });

            }
            function onButtonLeave() {
                button.style.background = 'lightgray';
                button.style.color = 'black'
                button.addEventListener('mouseenter', onButtonEnter, { once: true });
            }
            // Disabling the default right click menu
            button.oncontextmenu = () => { return false; };
            buttons.push(button);
        });

        return buttons;
    }


    /**
     * Removes the active menu
     */
    clearMenu() {
        if (!this._activeMenu)
            return;

        this._activeMenu.remove();
        this._activeMenu = null;
    }

    setContainer(container) {
        this.clearMenu();
        this._parentContainer = container;
    }
}